import { Field, FieldContent, FieldDescription } from "@design-system/shadcn";
import type { LucideIcon } from "lucide-react";
import React, { useCallback, useMemo, useState, type ReactNode } from "react";

import { useFieldWithSlots } from "../hooks/use-app-form";

interface SelectFieldOption {
  value: string;
  label: string;
  icon?: LucideIcon;
}

interface SelectFieldProps {
  label: ReactNode;
  selectId: string;
  testId: string;
  placeholder: string;
  options: SelectFieldOption[];
  searchPlaceholder?: string;
  emptyLabel?: string;
  selectedLabel?: string;
  onSearch?: (value: string) => void;
  isLoading?: boolean;
  onScrollToBottom?: () => void;
  isLoadingMore?: boolean;
  hasMoreOptions?: boolean;
  required?: boolean;
  disabled?: boolean;
  description?: ReactNode;
}

function SelectField({
  label,
  selectId,
  testId,
  placeholder,
  options,
  searchPlaceholder,
  emptyLabel,
  selectedLabel,
  onSearch,
  isLoading,
  onScrollToBottom,
  isLoadingMore,
  hasMoreOptions,
  required = false,
  disabled = false,
  description,
}: SelectFieldProps) {
  const field = useFieldWithSlots<string>();
  const selectedValue = field.state.value;

  const [localSearch, setLocalSearch] = useState("");
  const handleSearch = useCallback(
    (value: string) => {
      if (onSearch) onSearch(value);
      else setLocalSearch(value);
    },
    [onSearch]
  );

  const visibleOptions = useMemo(() => {
    if (onSearch) return options;

    const term = localSearch.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(term)
    );
  }, [onSearch, options, localSearch]);

  const selected = options.find((option) => option.value === selectedValue);
  const SelectedIcon = selected?.icon;
  const displayLabel = selected?.label ?? selectedLabel;

  return (
    <Field data-invalid={field.state.meta.errors.length > 0 || undefined}>
      <field.Label htmlFor={selectId} required={required}>
        {label}
      </field.Label>
      <FieldContent>
        <field.Combobox
          onSearch={handleSearch}
          isLoading={isLoading}
          onScrollToBottom={onScrollToBottom}
          isLoadingMore={isLoadingMore}
          hasMoreOptions={hasMoreOptions}
          disabled={disabled}
        >
          <field.Combobox.Trigger
            id={selectId}
            className="w-full"
            aria-required={required || undefined}
            data-testid={testId}
          >
            {selectedValue && displayLabel ? (
              <span className="flex min-w-0 items-center gap-2">
                {SelectedIcon && (
                  <SelectedIcon className="size-4 shrink-0" aria-hidden />
                )}
                <span className="truncate">{displayLabel}</span>
              </span>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </field.Combobox.Trigger>
          <field.Combobox.Content>
            <field.Combobox.Input placeholder={searchPlaceholder} />
            <field.Combobox.List>
              <field.Combobox.Empty>{emptyLabel}</field.Combobox.Empty>
              {visibleOptions.map(
                ({ value, label: optionLabel, icon: Icon }) => (
                  <field.Combobox.ListItem key={value} value={value}>
                    {Icon && <Icon className="size-4 shrink-0" aria-hidden />}
                    {optionLabel}
                  </field.Combobox.ListItem>
                )
              )}
            </field.Combobox.List>
          </field.Combobox.Content>
        </field.Combobox>
        <field.Error />
        {description && <FieldDescription>{description}</FieldDescription>}
      </FieldContent>
    </Field>
  );
}

export { SelectField };
export type { SelectFieldOption, SelectFieldProps };
